import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedTime } from 'react-intl';
import { getTimeFromHour } from '../../../../../modules/datetime';
import { compose, pure } from '../../../../../src/web/utils/react';
import Translation from '../../../../components/stateless/Translation';

const propTypes = {
  typeId: PropTypes.string,
  note: PropTypes.string,
  hours: PropTypes.arrayOf(PropTypes.shape({
    day: PropTypes.string.isRequired,
    open: PropTypes.number.isRequired,
    close: PropTypes.number.isRequired,
  })),
  use24HourTime: PropTypes.bool.isRequired,
  accessPassport: PropTypes.bool,
  timezone: PropTypes.string,
};

const defaultProps = {
  typeId: undefined,
  note: undefined,
  hours: undefined,
  accessPassport: false,
  timezone: undefined,
};

const daysOfTheWeek = {
  Sunday: { id: 'weekday.sun', defaultMessage: 'Sun' },
  Monday: { id: 'weekday.mon', defaultMessage: 'Mon' },
  Tuesday: { id: 'weekday.tue', defaultMessage: 'Tue' },
  Wednesday: { id: 'weekday.wed', defaultMessage: 'Wed' },
  Thursday: { id: 'weekday.thu', defaultMessage: 'Thurs' },
  Friday: { id: 'weekday.fri', defaultMessage: 'Fri' },
  Saturday: { id: 'weekday.sat', defaultMessage: 'Sat' },
};

function locationOpen(timezone, open, close) {
  // Check for closed state
  if (open === -1 || close === -1) {
    return false;
  }
  // Check for 24-hour operation (when open equals close)
  if (open === close) {
    return true;
  }
  const now = new Date();
  const options = { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false };
  const currentTimeString = now.toLocaleTimeString('en-US', options);
  const [hours, minutes] = currentTimeString.split(':').map(Number);
  const currentTime = Number((hours + minutes / 60).toFixed(2));

  return currentTime >= open && currentTime < close;
}

function rollUpHours(id, hours, use24HourTime, onlyToday, timezone) {
  const [first, ...rest] = hours;
  const formattedHours = [...rest, first];
  const today = Object.keys(daysOfTheWeek)[(new Date()).getDay()];
  const hoursForToday = formattedHours.filter(({ day }) => day === today);
  const hoursToShow = onlyToday ? hoursForToday : formattedHours;
  const closedAllDayOnlyToday = onlyToday ? 'All Day' : 'Closed';
  const closedAllDayOnlyTodayId = onlyToday ? 'allDay' : 'closed';
  const openAllDayOnlyToday = onlyToday ? 'All Day' : 'Open 24 Hours';
  const openAllDayOnlyTodayId = onlyToday ? 'allDay' : 'open24Hours';

  return hoursToShow.map(({ day, open, close }) => {
    const fontWeight = (day === today && !onlyToday) ? 'bold' : 'normal';

    let openHours;
    if (open === -1 || close === -1) {
      openHours = <span style={{ fontWeight }}><Translation id={`widgets.hours.hoursTable.${closedAllDayOnlyTodayId}`} defaultMessage={closedAllDayOnlyToday} /></span>;
    } else if (open === close) {
      openHours = <span style={{ fontWeight }}><Translation id={`widgets.hours.hoursTable.${openAllDayOnlyTodayId}`} defaultMessage={openAllDayOnlyToday} /></span>;
    } else {
      openHours = (
        <span className="lowercase">
          <span style={{ fontWeight }}>
            <FormattedTime value={getTimeFromHour(open)} hour12={!use24HourTime} />
          </span>
          {' - '}
          <span style={{ fontWeight }}>
            <FormattedTime value={getTimeFromHour(close)} hour12={!use24HourTime} />
          </span>
        </span>
      );
    }

    const isOpen = locationOpen(timezone, open, close);
    const todayComponent = (isOpen) ? (
      <span style={{ color: '#188038' }}>
        <Translation id="widgets.hours.hoursTable.openNow" defaultMessage="Open Now" />
      </span>
    ) : (
      <span style={{ color: '#b71000' }}>
        <Translation id="widgets.hours.hoursTable.closed" defaultMessage="Closed" />
      </span>
    );
    const dayComponent = onlyToday ? todayComponent : (
      <>
        {day === today && <span style={{ fontWeight: 'bold' }}><Translation {...daysOfTheWeek[day]} /> (<Translation id="widgets.hours.hoursTable.today" defaultMessage="Today" />)</span>}
        {day !== today && <span><Translation {...daysOfTheWeek[day]} /></span>}
      </>
    );

    return {
      day: dayComponent,
      openHours,
    };
  });
}

const SMHoursTable = ({ typeId, note, hours, use24HourTime, accessPassport, timezone }) => {
  if (!hours || !hours.length) {
    return false;
  }

  const [onlyToday] = useState(false);
  const groups = useMemo(() => rollUpHours(typeId, hours, use24HourTime, onlyToday, timezone),
    [typeId, hours, use24HourTime, onlyToday, timezone]);
  return (
    <>
      {note ? (
        <div className="mb-4 text-right">
          {note}
        </div>
      ) : (
        <div className="mb-4 text-right">
          <Translation
            id={`widgets.hours.typeOfHours.${typeId}`}
            defaultMessage="The office’s hours of operation"
          />
        </div>
      )}
      {
        !onlyToday && (
          <ul className="m-0 p-0 w-full">
            {groups.map(({ day, openHours }) => (
              <li key={day} style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                {day}
                {openHours}
              </li>
            ))}
          </ul>
        )
      }
      {
        accessPassport && (
          <div className="mt-4 text-right">
            <Translation
              id="widgets.hours.twentyFourHourPassport"
              defaultMessage="24 hour access pass available"
            />
          </div>
        )
      }

    </>
  );
};

SMHoursTable.propTypes = propTypes;
SMHoursTable.defaultProps = defaultProps;

export default compose(
  pure(),
)(SMHoursTable);
